/**
 * ContractorJobsListPage - Elite Contractor Dashboard
 * ====================================================
 * Full job list with search, filters, and inline actions.
 * Accessible via magic link token (no login required).
 * Uses unified JobCard component for consistent UI.
 */
import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import JobCard from "../components/JobCard";

const API = process.env.REACT_APP_BACKEND_URL || "";

// ============================================================================
// KPI CARD COMPONENT
// ============================================================================

function KPICard({ label, value, color = "teal", icon }) {
  const colorClasses = {
    teal: "border-teal-200 bg-teal-50 text-teal-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    blue: "border-blue-200 bg-blue-50 text-blue-700",
    green: "border-green-200 bg-green-50 text-green-700",
    red: "border-red-200 bg-red-50 text-red-700",
  };

  return (
    <div className={`rounded-2xl border p-4 ${colorClasses[color]}`} data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium opacity-80">{label}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
        </div>
        {icon && <div className="text-2xl opacity-60">{icon}</div>}
      </div>
    </div>
  );
}

// ============================================================================
// FILTER CHIP COMPONENT
// ============================================================================

function FilterChip({ label, active, onClick, count }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
        active
          ? "bg-teal-600 text-white shadow-sm"
          : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
      }`}
      data-testid={`filter-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      {label} {count !== undefined && `(${count})`}
    </button>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function ContractorJobsListPage() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const token = params.get("token");

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [contractor, setContractor] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [acting, setActing] = useState(null); // Track which job is being acted upon

  // Filter state
  const [filters, setFilters] = useState({
    search: "",
    stage: "all",
    priority: "all",
  });

  // My Day / Route state
  const [dayJobs, setDayJobs] = useState([]);
  const [dayRouteSummary, setDayRouteSummary] = useState(null);
  const [activeTab, setActiveTab] = useState("all"); // all, today, upcoming, overdue
  const [loadingDay, setLoadingDay] = useState(false);

  // ============================================================================
  // LOAD JOBS
  // ============================================================================

  const loadJobs = useCallback(async () => {
    if (!token) {
      setError("Invalid access link - no token provided");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/contractor/jobs-list?token=${encodeURIComponent(token)}`);
      
      if (res.data.ok) {
        setContractor(res.data.contractor);
        setJobs(res.data.jobs || []);
      } else {
        setError(res.data.error || "Failed to load jobs");
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setError(err.response?.data?.detail || err.message || "Failed to load jobs");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadDayJobs = useCallback(async () => {
    if (!token) return;
    
    setLoadingDay(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await axios.get(`${API}/api/contractor/day-route?token=${encodeURIComponent(token)}&date=${today}`);
      
      if (res.data.ok) {
        setDayJobs(res.data.ordered_jobs || []);
        setDayRouteSummary({
          total_jobs: res.data.total_jobs,
          total_sites: res.data.total_sites,
          estimated_distance_miles: res.data.estimated_distance_miles,
          site_groups: res.data.site_groups
        });
      }
    } catch (err) {
      console.error("Failed to load day jobs:", err);
    } finally {
      setLoadingDay(false);
    }
  }, [token]);

  useEffect(() => {
    loadJobs();
    loadDayJobs();
  }, [loadJobs, loadDayJobs]);

  // ============================================================================
  // COMPUTED: FILTERED JOBS
  // ============================================================================

  const filteredJobs = useMemo(() => {
    let result = [...jobs];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(j =>
        j.site_name?.toLowerCase().includes(searchLower) ||
        j.asset_name?.toLowerCase().includes(searchLower) ||
        j.postcode?.toLowerCase().includes(searchLower) ||
        j.job_reference?.toLowerCase().includes(searchLower) ||
        j.description?.toLowerCase().includes(searchLower) ||
        j.job_type?.toLowerCase().includes(searchLower)
      );
    }

    // Stage filter
    if (filters.stage !== "all") {
      if (filters.stage === "active") {
        // Active = In Progress, Scheduled, Approved (work being done)
        result = result.filter(j => ["In Progress", "Scheduled", "Approved", "Work In Progress"].includes(j.status));
      } else if (filters.stage === "completed") {
        result = result.filter(j => ["Completed", "Invoice Submitted"].includes(j.status));
      } else if (filters.stage === "new") {
        // New = Awaiting action from contractor (not yet accepted/scheduled)
        result = result.filter(j => ["Job Created", "Awaiting Quote", "Assigned", "Quote Submitted", "Quote Awaiting Approval"].includes(j.status));
      } else if (filters.stage === "scheduled") {
        result = result.filter(j => j.status === "Scheduled");
      }
    }

    // Priority filter
    if (filters.priority !== "all") {
      result = result.filter(j => j.priority?.toLowerCase() === filters.priority.toLowerCase());
    }

    return result;
  }, [jobs, filters]);

  // ============================================================================
  // COMPUTED: KPIs
  // ============================================================================

  const kpis = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    
    const newJobs = jobs.filter(j => ["Job Created", "Awaiting Quote", "Assigned", "Quote Submitted", "Quote Awaiting Approval"].includes(j.status));
    const activeJobs = jobs.filter(j => ["In Progress", "Scheduled", "Approved", "Work In Progress"].includes(j.status));
    const completedJobs = jobs.filter(j => ["Completed", "Invoice Submitted"].includes(j.status));
    const highPriority = jobs.filter(j => j.priority?.toLowerCase() === "high" || j.priority?.toLowerCase() === "urgent");
    
    // Overdue jobs (due_date or scheduled_date in the past, not completed)
    const overdueJobs = jobs.filter(j => {
      if (["Completed", "Closed", "Cancelled", "Invoice Submitted"].includes(j.status)) return false;
      const dueDate = j.due_date || j.scheduled_date;
      if (!dueDate) return false;
      return dueDate.split('T')[0] < today;
    });
    
    // Today's jobs
    const todayJobs = jobs.filter(j => {
      const schedDate = j.scheduled_date || j.due_date;
      if (!schedDate) return false;
      return schedDate.split('T')[0] === today;
    });

    return {
      total: jobs.length,
      new: newJobs.length,
      active: activeJobs.length,
      completed: completedJobs.length,
      highPriority: highPriority.length,
      overdue: overdueJobs.length,
      today: todayJobs.length,
    };
  }, [jobs]);

  // Jobs filtered by active tab
  const tabFilteredJobs = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    
    switch (activeTab) {
      case "today":
        return dayJobs;
      case "overdue":
        return jobs.filter(j => {
          if (["Completed", "Closed", "Cancelled", "Invoice Submitted"].includes(j.status)) return false;
          const dueDate = j.due_date || j.scheduled_date;
          if (!dueDate) return false;
          return dueDate.split('T')[0] < today;
        });
      case "upcoming":
        return jobs.filter(j => {
          if (["Completed", "Closed", "Cancelled"].includes(j.status)) return false;
          const schedDate = j.scheduled_date || j.due_date;
          if (!schedDate) return false;
          return schedDate.split('T')[0] > today;
        });
      default:
        return filteredJobs;
    }
  }, [activeTab, dayJobs, jobs, filteredJobs]);

  // ============================================================================
  // ACTION HANDLERS
  // ============================================================================

  const handleAccept = async (jobId) => {
    setActing(jobId);
    try {
      const res = await axios.post(`${API}/api/contractor/job/${jobId}/respond?token=${encodeURIComponent(token)}`, {
        action: "accept"
      });
      
      if (res.data.ok || res.data.success) {
        toast.success("Job accepted!");
        loadJobs();
      } else {
        toast.error(res.data.error || "Failed to accept job");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept job");
    } finally {
      setActing(null);
    }
  };

  const handleDecline = async (jobId) => {
    const reason = window.prompt("Please provide a reason for declining:");
    if (!reason) return;

    setActing(jobId);
    try {
      const res = await axios.post(`${API}/api/contractor/job/${jobId}/respond?token=${encodeURIComponent(token)}`, {
        action: "decline",
        decline_reason: reason
      });
      
      if (res.data.ok || res.data.success) {
        toast.success("Job declined");
        loadJobs();
      } else {
        toast.error(res.data.error || "Failed to decline job");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to decline job");
    } finally {
      setActing(null);
    }
  };

  const handleOpenJob = (jobId) => {
    window.location.href = `/contractor/job/${jobId}?token=${encodeURIComponent(token)}`;
  };

  // ============================================================================
  // MAP JOB TO UNIFIED FORMAT
  // ============================================================================

  const mapJobToUnified = (job) => ({
    job_id: job.id,
    job_reference: job.job_reference,
    park_name: job.site_name,
    unit_name: job.asset_name,
    full_address: `${job.site_name || ''}, ${job.asset_name || ''}`.replace(/^, |, $/g, ''),
    postcode: job.postcode,
    job_type: job.job_type || job.job_type_label,
    priority: job.priority,
    description: job.description,
    created_date: job.created_at,
    scheduled_date: job.scheduled_date,
    stage: job.status,
    status: job.status,
  });

  // ============================================================================
  // LOADING STATE
  // ============================================================================

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-10 w-10 text-teal-600 mx-auto mb-4" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="text-slate-600">Loading your jobs...</p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // ERROR STATE
  // ============================================================================

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-slate-800 mb-2">Access Error</h2>
          <p className="text-slate-600 mb-6">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-2xl font-semibold transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contractor-jobs-list-page">
      
      {/* Header */}
      <header className="bg-gradient-to-r from-teal-600 to-teal-700 text-white">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3 mb-4">
            <img
              src="https://customer-assets.emergentagent.com/job_park-case-tracker/artifacts/cww8tyf2_LHS%20LOGO%202%20.png"
              alt="LHS"
              className="h-10"
            />
            <span className="text-lg font-semibold">Contractor Portal</span>
          </div>
          <h1 className="text-2xl font-bold">My Jobs Dashboard</h1>
          <p className="text-teal-100 mt-1">{contractor?.name}</p>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <KPICard label="Today" value={kpis.today} color="teal" />
          <KPICard label="New Requests" value={kpis.new} color="amber" />
          <KPICard label="Active" value={kpis.active} color="blue" />
          <KPICard label="Overdue" value={kpis.overdue} color="red" />
          <KPICard label="Completed" value={kpis.completed} color="green" />
        </div>

        {/* Main Tabs */}
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-4">
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => setActiveTab("today")}
              className={`px-6 py-3 rounded-2xl font-semibold transition-all ${
                activeTab === "today"
                  ? "bg-teal-600 text-white shadow-md"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              data-testid="tab-my-day"
            >
              My Day {kpis.today > 0 && `(${kpis.today})`}
            </button>
            <button
              onClick={() => setActiveTab("all")}
              className={`px-6 py-3 rounded-2xl font-semibold transition-all ${
                activeTab === "all"
                  ? "bg-teal-600 text-white shadow-md"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              data-testid="tab-all-jobs"
            >
              All Jobs ({kpis.total})
            </button>
            <button
              onClick={() => setActiveTab("upcoming")}
              className={`px-6 py-3 rounded-2xl font-semibold transition-all ${
                activeTab === "upcoming"
                  ? "bg-teal-600 text-white shadow-md"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              data-testid="tab-upcoming"
            >
              Upcoming
            </button>
            <button
              onClick={() => setActiveTab("overdue")}
              className={`px-6 py-3 rounded-2xl font-semibold transition-all ${
                activeTab === "overdue"
                  ? "bg-red-600 text-white shadow-md"
                  : kpis.overdue > 0 
                    ? "bg-red-100 text-red-700 hover:bg-red-200"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              data-testid="tab-overdue"
            >
              Overdue {kpis.overdue > 0 && `(${kpis.overdue})`}
            </button>
          </div>

          {/* Route Summary for My Day tab */}
          {activeTab === "today" && dayRouteSummary && (
            <div className="bg-teal-50 border border-teal-200 rounded-2xl p-4 mb-4">
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <span className="font-semibold text-teal-800">{dayRouteSummary.total_jobs} jobs today</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  </svg>
                  <span className="text-teal-700">{dayRouteSummary.total_sites} sites</span>
                </div>
                {dayRouteSummary.estimated_distance_miles > 0 && (
                  <div className="flex items-center gap-2">
                    <svg className="h-5 w-5 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                    <span className="text-teal-700">~{dayRouteSummary.estimated_distance_miles} miles</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Search & Filters (only show for All Jobs tab) */}
        {activeTab === "all" && (
          <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-4 space-y-4">
            
            {/* Search */}
            <div className="relative">
              <svg className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search park, plot, postcode, reference, description..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full pl-12 pr-4 py-3 rounded-2xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                data-testid="search-input"
              />
            </div>

            {/* Filter Chips */}
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-slate-500 self-center mr-2">Stage:</span>
              <FilterChip 
                label="All" 
                active={filters.stage === "all"} 
                onClick={() => setFilters({ ...filters, stage: "all" })}
                count={jobs.length}
              />
              <FilterChip 
                label="New" 
                active={filters.stage === "new"} 
                onClick={() => setFilters({ ...filters, stage: "new" })}
                count={kpis.new}
              />
              <FilterChip 
                label="Active" 
                active={filters.stage === "active"} 
                onClick={() => setFilters({ ...filters, stage: "active" })}
                count={kpis.active}
              />
              <FilterChip 
                label="Scheduled" 
                active={filters.stage === "scheduled"} 
                onClick={() => setFilters({ ...filters, stage: "scheduled" })}
              />
              <FilterChip 
                label="Completed" 
                active={filters.stage === "completed"} 
                onClick={() => setFilters({ ...filters, stage: "completed" })}
                count={kpis.completed}
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-slate-500 self-center mr-2">Priority:</span>
              <FilterChip 
                label="All Priorities" 
                active={filters.priority === "all"} 
                onClick={() => setFilters({ ...filters, priority: "all" })}
              />
              <FilterChip 
                label="High/Urgent" 
                active={filters.priority === "high"} 
                onClick={() => setFilters({ ...filters, priority: "high" })}
              />
              <FilterChip 
                label="Normal" 
                active={filters.priority === "normal"} 
                onClick={() => setFilters({ ...filters, priority: "normal" })}
              />
              <FilterChip 
                label="Low" 
                active={filters.priority === "low"} 
                onClick={() => setFilters({ ...filters, priority: "low" })}
              />
            </div>
          </div>
        )}

        {/* Results Header */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">
            Showing <span className="font-semibold text-slate-700">{tabFilteredJobs.length}</span> {activeTab === "today" ? "jobs today" : `of ${jobs.length} jobs`}
          </p>
          <button
            onClick={() => { loadJobs(); loadDayJobs(); }}
            className="flex items-center gap-2 px-4 py-2 text-sm text-teal-600 hover:bg-teal-50 rounded-xl transition-all"
            data-testid="refresh-btn"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>

        {/* Jobs List */}
        {(activeTab === "today" && loadingDay) ? (
          <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-12 text-center">
            <svg className="animate-spin h-8 w-8 text-teal-600 mx-auto mb-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <p className="text-slate-500">Loading today's route...</p>
          </div>
        ) : tabFilteredJobs.length === 0 ? (
          <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-12 text-center">
            <svg className="h-16 w-16 text-slate-200 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h3 className="text-lg font-semibold text-slate-700 mb-2">
              {activeTab === "today" ? "No jobs scheduled for today" : 
               activeTab === "overdue" ? "No overdue jobs" :
               activeTab === "upcoming" ? "No upcoming jobs" : "No jobs found"}
            </h3>
            <p className="text-slate-500">
              {activeTab === "all" && (filters.search || filters.stage !== "all" || filters.priority !== "all")
                ? "Try adjusting your filters"
                : activeTab === "today" ? "Check back later or view all jobs"
                : "No jobs have been assigned yet"
              }
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {tabFilteredJobs.map((job, index) => {
              const unifiedJob = mapJobToUnified(job);
              const canRespond = ["Job Created", "Assigned", "Awaiting Quote"].includes(job.status);
              const canSchedule = job.status === "Assigned" || job.status === "Approved";
              const canQuote = job.status === "Awaiting Quote" || job.status === "Assigned";

              return (
                <div key={job.id} className="space-y-3" data-testid={`job-item-${job.id}`}>
                  {/* Route sequence badge for My Day tab */}
                  {activeTab === "today" && (
                    <div className="flex items-center gap-2">
                      <span className="bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                        Job {job.route_sequence || index + 1} of {tabFilteredJobs.length}
                      </span>
                      {index === 0 && (
                        <span className="bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                          NEXT
                        </span>
                      )}
                    </div>
                  )}
                  <JobCard
                    job={unifiedJob}
                    variant="contractor"
                    actions={{
                      onAccept: canRespond ? () => handleAccept(job.id) : undefined,
                      onDecline: canRespond ? () => handleDecline(job.id) : undefined,
                      onSchedule: canSchedule ? () => handleOpenJob(job.id) : undefined,
                      onQuote: canQuote ? () => handleOpenJob(job.id) : undefined,
                    }}
                    footer={
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => handleOpenJob(job.id)}
                          className="flex-1 bg-slate-900 hover:bg-slate-800 text-white rounded-2xl py-3 px-4 font-semibold text-sm transition-colors"
                          data-testid={`open-job-${job.id}`}
                        >
                          Open Full Job →
                        </button>
                        {job.requires_certificate && (
                          <div className={`flex items-center gap-1 px-3 py-2 rounded-2xl text-xs font-medium ${
                            job.mandatory_certificate_satisfied 
                              ? "bg-green-50 text-green-700 border border-green-200" 
                              : "bg-amber-50 text-amber-700 border border-amber-200"
                          }`}>
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                            </svg>
                            {job.mandatory_certificate_satisfied ? "Cert OK" : "Cert Required"}
                          </div>
                        )}
                      </div>
                    }
                  />
                  
                  {acting === job.id && (
                    <div className="bg-teal-50 border border-teal-200 rounded-2xl p-3 flex items-center gap-2 text-sm text-teal-700">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Processing...
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-8 py-4">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm text-slate-500">
          Leisure Home Solutions Ltd | admin@lhsportal.uk | 07557 017694
        </div>
      </footer>
    </div>
  );
}
